import { useCallback, useEffect, useState } from "react";

import type { DemoScene } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { useI18n } from "@/i18n/use-i18n";
import { cn } from "@/lib/cn";

interface CustomerPreviewProps {
  productName: string;
  scenes: DemoScene[];
  onClose: () => void;
}

/**
 * The in-conversation "Kundenvorschau" overlay: a short guided walkthrough of
 * how the product looks for the customer.
 */
export function CustomerPreview({ productName, scenes, onClose }: CustomerPreviewProps) {
  const { t } = useI18n();
  const [index, setIndex] = useState(0);
  const last = scenes.length - 1;

  const go = useCallback(
    (delta: number) => {
      setIndex((current) => {
        const next = current + delta;
        if (next > last) {
          onClose();
          return current;
        }
        return Math.max(0, Math.min(last, next));
      });
    },
    [last, onClose],
  );

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") go(1);
      if (event.key === "ArrowLeft") go(-1);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [go, onClose]);

  if (scenes.length === 0) return null;
  const scene = scenes[index];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("preview.title", { product: productName })}
      onClick={(event) => {
        // Only a click on the backdrop itself closes, not one that bubbled up
        // from the dialog contents.
        if (event.target === event.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 grid place-items-center bg-navy-deep/70 p-2 backdrop-blur-sm sm:p-4"
    >
      <div className="flex max-h-[90dvh] w-full max-w-[720px] flex-col overflow-hidden rounded-[18px] bg-surface shadow-modal">
        <header className="flex items-start gap-4 border-b border-line px-5 py-4 sm:px-6 sm:py-4.5">
          <div className="min-w-0 flex-1">
            <h3 className="text-[17px] font-bold sm:text-[19px]">{t("preview.title", { product: productName })}</h3>
            <div className="text-[13px] text-muted">{t("preview.subtitle")}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="shrink-0 rounded border-0 bg-transparent px-2 py-1 text-[18px] text-muted hover:bg-line-soft hover:text-ink"
          >
            ✕
          </button>
        </header>

        {/* Progress dashes */}
        <div className="flex gap-1.5 px-6 pt-4">
          {scenes.map((item, itemIndex) => (
            <span
              key={item.title}
              className={cn(
                "h-[3px] flex-1 rounded-full transition-colors",
                itemIndex <= index ? "bg-teal" : "bg-line",
              )}
            />
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">
          <div className="mb-1.5 font-mono text-[11px] tracking-[0.09em] text-teal-deep uppercase">
            {t("preview.scene", { index: index + 1, total: scenes.length })}
          </div>
          <h2 className="mb-1 text-[19px] font-bold sm:text-[22px]">{scene.title}</h2>
          {scene.subtitle && <p className="mb-3 text-[13.5px] text-muted">{scene.subtitle}</p>}
          <p className="mb-4.5 max-w-[62ch] text-[14.5px]">{scene.body}</p>

          {/* Faked product chrome */}
          <div className="overflow-hidden rounded-card border border-line">
            {scene.mock_bar && (
              <div className="border-b border-line bg-navy px-3.5 py-2 font-mono text-[11.5px] text-white/85">
                {scene.mock_bar}
              </div>
            )}

            <div className="bg-page">
              {scene.mock_text && (
                <pre className="m-0 overflow-x-auto px-3.5 py-3 font-mono text-[12px] whitespace-pre-wrap">
                  {scene.mock_text}
                </pre>
              )}

              {scene.mock_fields.map((field) => (
                <div
                  key={field.label}
                  className="flex flex-wrap justify-between gap-3 border-b border-line-soft px-3.5 py-2.5 text-[13px] last:border-0"
                >
                  <span className="text-muted">{field.label}</span>
                  <span
                    className={cn(
                      "flex items-center gap-2 font-medium",
                      field.tone === "warn" && "text-warn",
                      field.tone === "accent" && "text-teal-deep",
                    )}
                  >
                    {field.value}
                    {field.badge && (
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-[10.5px] font-semibold",
                          field.tone === "warn"
                            ? "bg-warn-wash text-warn"
                            : "bg-ok-wash text-ok",
                        )}
                      >
                        {field.badge}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <footer className="flex items-center gap-3 border-t border-line px-5 py-3.5 sm:px-6 sm:py-4">
          <Button variant="ghost" size="sm" disabled={index === 0} onClick={() => go(-1)}>
            {t("common.back")}
          </Button>
          <Spacer />
          <span className="font-mono text-[12px] text-muted">
            {index + 1} / {scenes.length}
          </span>
          <Spacer />
          <Button size="sm" onClick={() => go(1)}>
            {t(index === last ? "common.close" : "common.next")}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function Spacer() {
  return <span className="flex-1" />;
}
